import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../../services/api/apiService";

/**
 * Generic OAuth callback shell.
 * Props:
 *   platform     — human-readable name shown in the UI
 *   endpoint     — POST endpoint on the backend e.g. "/api/ads-auth/meta/callback"
 *   buildPayload — (queryParams: URLSearchParams) => object
 *   redirectTo   — where to go on success (default: /dashboard/connected-platforms)
 *   onSuccess    — optional best-effort side effect fired after a successful
 *                  connect, before the redirect (e.g. trigger an immediate
 *                  sync so stale error banners don't wait for the next cron)
 */
const OAuthCallbackShell = ({
  platform,
  endpoint,
  buildPayload,
  redirectTo = "/dashboard/connected-platforms",
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const params = new URLSearchParams(window.location.search);

    const oauthError = params.get("error");
    if (oauthError) {
      const desc = params.get("error_description") || oauthError;
      setErrorMsg(desc);
      setStatus("error");
      return;
    }

    const payload = buildPayload(params);
    const missing = Object.entries(payload).filter(([, v]) => !v).map(([k]) => k);
    if (missing.length) {
      setErrorMsg(`Missing OAuth parameters: ${missing.join(", ")}.`);
      setStatus("error");
      return;
    }

    postRequest(endpoint, payload)
      .then((res) => {
        if (res?.success) {
          setStatus("success");
          onSuccess?.();
          setTimeout(() => navigate(redirectTo), 1500);
        } else {
          setErrorMsg(res?.message || "Authentication failed. Please try again.");
          setStatus("error");
        }
      })
      .catch((err) => {
        setErrorMsg(
          err?.response?.data?.message ||
          err?.message ||
          "An unexpected error occurred."
        );
        setStatus("error");
      });
  }, [endpoint, buildPayload, redirectTo, navigate, onSuccess]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-14 h-14 rounded-full border-4 border-[#221C51] border-t-transparent animate-spin mb-5" />
        <p className="text-base font-semibold text-[#221C51]">Connecting {platform}…</p>
        <p className="text-sm text-gray-400 mt-1">Please wait, this only takes a moment.</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-lg font-bold text-gray-800">{platform} Connected!</p>
        <p className="text-sm text-gray-400 mt-1">Redirecting to your dashboard…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <p className="text-lg font-bold text-gray-800 mb-1">Connection Failed</p>
      <p className="text-sm text-gray-500 mb-5 text-center max-w-sm">{errorMsg}</p>
      <div className="flex gap-3">
        <button
          onClick={() => navigate("/dashboard/connected-platforms")}
          className="px-5 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition"
        >
          Back to Platforms
        </button>
        <button
          onClick={() => navigate("/dashboard/connected-platforms")}
          className="px-5 py-2 rounded-lg bg-[#221C51] text-white text-sm font-medium hover:bg-[#2d267a] transition"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

export default OAuthCallbackShell;
